import React from 'react';
import TableContainer from './TableContainer';
import { Table, TableHeader, TableBody, TableRow, TableCell } from './Table';

export interface Column {
  key: string;
  label: string;
  width?: string; // For desktop table widths
  className?: string;
  render?: (value: any, item: any) => React.ReactNode;
}

export interface ResponsiveTableProps {
  columns: Column[];
  data: any[];
  onRowClick?: (item: any) => void;
  keyExtractor: (item: any) => string;
  cardRenderer?: (item: any) => React.ReactNode; // Custom card renderer
  mobileColumns?: string[]; // Which columns to show in mobile card grid
  className?: string;
}

const ResponsiveTable: React.FC<ResponsiveTableProps> = ({
  columns,
  data,
  onRowClick,
  keyExtractor,
  cardRenderer,
  mobileColumns,
  className = ''
}) => {
  // Default mobile columns: show first 4 columns in a 2x2 grid
  const mobileCols = mobileColumns || columns.slice(0, 4).map(col => col.key);

  const defaultCardRenderer = (item: any) => {
    // Find the first column to use as the title
    const titleColumn = columns[0];
    const titleValue = titleColumn.render 
      ? titleColumn.render(item[titleColumn.key], item)
      : item[titleColumn.key];

    // Get mobile columns data
    const mobileColumnData = mobileCols.map(colKey => {
      const column = columns.find(col => col.key === colKey);
      if (!column) return null;
      
      const value = column.render 
        ? column.render(item[column.key], item)
        : item[column.key];
      
      return {
        label: column.label,
        value
      };
    }).filter(Boolean);

    return (
      <div
        onClick={() => onRowClick?.(item)}
        className="bg-bg-secondary border border-border-primary rounded-lg p-4 cursor-pointer hover:bg-bg-tertiary transition-colors"
      >
        {/* Title row */}
        <div className="mb-3">
          <h3 className="text-base font-medium text-text-primary">
            {titleValue}
          </h3>
        </div>
        
        {/* Grid of other values */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          {mobileColumnData.slice(1).map((col, index) => (
            <div key={index}>
              <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">
                {col?.label}
              </div>
              <div className="text-text-primary">
                {col?.value || '-'}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile Cards */}
      <div className={`sm:hidden space-y-4 ${className}`}>
        {data.map((item) => (
          <div key={keyExtractor(item)}>
            {cardRenderer ? cardRenderer(item) : defaultCardRenderer(item)}
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <TableContainer className={`hidden sm:block ${className}`}>
        <Table className="table-fixed w-full">
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={{ width: column.width || 'auto' }} />
            ))}
          </colgroup>
          <TableHeader>
            {columns.map((column) => (
              <TableCell 
                key={column.key} 
                variant="th"
                className={column.className}
              >
                {column.label}
              </TableCell>
            ))}
          </TableHeader>
          <TableBody>
            {data.map((item) => (
              <TableRow
                key={keyExtractor(item)}
                onClick={() => onRowClick?.(item)}
                className={onRowClick ? "cursor-pointer" : ""}
              >
                {columns.map((column) => (
                  <TableCell 
                    key={column.key}
                    className={column.className || "text-sm text-text-secondary"}
                  >
                    {column.render 
                      ? column.render(item[column.key], item)
                      : item[column.key] || '-'
                    }
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ResponsiveTable;